import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Wallet, TrendingUp, CheckCircle2, ShieldCheck, BadgeDollarSign, Users } from "lucide-react";
import AdminLayout from "../AdminLayout";
import { adminAxios } from "../api";

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState({
    items: [],
    total_payout: 0,
    completion_bonus: 200,
    admin_earned: 0,
    total_technicians: 0,
    total_completed_jobs: 0,
    gross_revenue: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await adminAxios.get("/payouts");
        setPayouts(data || {
          items: [],
          total_payout: 0,
          completion_bonus: 200,
          admin_earned: 0,
          total_technicians: 0,
          total_completed_jobs: 0,
          gross_revenue: 0,
        });
      } catch (err) {
        console.error("Failed to load payouts", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <AdminLayout title="Payouts"><div className="text-slate-500">Loading payouts…</div></AdminLayout>;
  }

  return (
    <AdminLayout title="Payouts">
      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-[#1A3C6E] via-[#1A3C6E] to-[#264a87] p-5 text-white">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-sky-100/80">Payout overview</p>
              <h2 className="mt-2 font-display text-3xl font-extrabold">₹{Number(payouts.total_payout || 0).toLocaleString("en-IN")}</h2>
              <p className="mt-1 text-sm text-sky-100/80">{payouts.total_completed_jobs || 0} completed jobs across {payouts.total_technicians || 0} technicians</p>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-sm text-sky-50 backdrop-blur-sm">
              <ShieldCheck className="w-4 h-4" />
              Historical earnings preserved
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Payout / job" value={`₹${Number(payouts.completion_bonus || 0).toLocaleString("en-IN")}`} icon={BadgeDollarSign} tone="blue" />
          <StatCard label="Total payouts" value={`₹${Number(payouts.total_payout || 0).toLocaleString("en-IN")}`} icon={TrendingUp} tone="amber" />
          <StatCard label="Gross revenue" value={`₹${Number(payouts.gross_revenue || 0).toLocaleString("en-IN")}`} icon={CheckCircle2} tone="green" />
          <StatCard label="Admin earned" value={`₹${Number(payouts.admin_earned || 0).toLocaleString("en-IN")}`} icon={Wallet} tone="slate" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div>
              <h3 className="font-display font-bold text-slate-900">Technician payout ledger</h3>
              <p className="text-xs text-slate-500 mt-1">Live payout summary by technician</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-slate-600">
                <Users className="w-3.5 h-3.5" />
                {payouts.total_technicians || 0} technicians
              </div>
              <button className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <Download className="w-4 h-4" /> Export
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-5 py-3">Technician</th>
                  <th className="px-5 py-3">Jobs</th>
                  <th className="px-5 py-3">Gross value</th>
                  <th className="px-5 py-3">Payout</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {(payouts.items || []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-slate-400">No payout records found.</td>
                  </tr>
                )}
                {(payouts.items || []).map((entry) => (
                  <tr key={entry.technician_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-slate-900">{entry.technician_name}</div>
                      <div className="text-xs text-slate-500">{entry.technician_id}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{entry.completed_jobs}</td>
                    <td className="px-5 py-3 text-slate-700">₹{Number(entry.booking_total || 0).toLocaleString("en-IN")}</td>
                    <td className="px-5 py-3 font-semibold text-slate-900">₹{Number(entry.payout_total || 0).toLocaleString("en-IN")}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs font-semibold">Ready</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Link to="/admin/bookings?status=completed" className="inline-flex items-center gap-1 text-xs font-semibold text-[#1A3C6E] hover:text-[#FF6B2B]">
                        View jobs <ArrowRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const tones = {
    blue: "bg-[#1A3C6E]/10 text-[#1A3C6E]",
    amber: "bg-[#ffedd5] text-[#c2410c]",
    green: "bg-[#dcfce7] text-[#166534]",
    slate: "bg-slate-100 text-slate-700",
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
          <div className="mt-2 font-display font-bold text-2xl text-slate-900">{value}</div>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tones[tone] || tones.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}
